import type { CheckoutResult } from '@/services/checkout';
import type { EpaycoPayPayload } from '@/services/gateways';

// =============================================================
// Pago pendiente de ePayco
// =============================================================
// Cuando se crea un pedido con ePayco, la orden ya existe en la base (con su
// reserva de stock) pero el pago aún puede no completarse. Guardamos aquí los
// datos mínimos para poder RETOMAR ese mismo pago sin crear un pedido nuevo,
// de modo que si el cliente sale de ePayCo y vuelve, no se quede sin forma de
// pagar (y sin carrito, porque la orden ya se registró).

const STORAGE_KEY = 'pcp_pending_epayco_payment';

// El pago pendiente deja de ser válido después de este tiempo (evita que un
// pedido viejo atrape al cliente en la pantalla de pago).
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PendingEpaycoPayment {
  order: CheckoutResult;
  // Grupo completo de pedidos de una compra combinada (normal + producción).
  // Si falta, se asume que `order` es el único.
  orders?: CheckoutResult[];
  payload: EpaycoPayPayload;
  savedAt: string;
  // Claves "productId|sizeId" de los productos que formaban parte de ESTE pedido,
  // para vaciar exactamente esos ítems del carrito cuando el pago se apruebe
  // (sin borrar artículos que el cliente haya agregado después).
  orderedKeys?: string[];
}

export function savePendingEpayco(payment: Omit<PendingEpaycoPayment, 'savedAt'>): void {
  try {
    const data: PendingEpaycoPayment = {
      ...payment,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // almacenamiento no disponible, ignorar
  }
}

export function loadPendingEpayco(): PendingEpaycoPayment | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingEpaycoPayment;
    if (!parsed?.order?.orderNumber || !parsed?.payload) return null;
    const savedAt = new Date(parsed.savedAt ?? '').getTime();
    if (!Number.isFinite(savedAt) || Date.now() - savedAt > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingEpayco(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignorar
  }
}

// =============================================================
// Financiamiento pendiente de Sistecrédito
// =============================================================
// Igual que ePayco: al iniciar el financiamiento el pedido YA existe en la base
// (con su reserva de stock), pero la aprobación llega después (por webhook).
// Guardamos aquí los datos mínimos para poder RETOMAR ese MISMO pedido al volver
// al checkout, sin crear uno nuevo (evita pedidos duplicados).
//
// Además guardamos el dato de CONTACTO para poder consultar el estado real del
// pedido de forma automática y saber cuándo el financiamiento fue aprobado, y
// así vaciar el carrito recién en ese momento.

const SISTECREDITO_STORAGE_KEY = 'pcp_pending_sistecredito_payment';

export interface PendingSistecreditoPayment {
  order: CheckoutResult;
  // Grupo completo de pedidos de una compra combinada (normal + producción).
  orders?: CheckoutResult[];
  amount: number;
  // Dato de contacto (celular/correo/documento) para verificar el estado del
  // pedido y detectar automáticamente la aprobación del financiamiento.
  contact: string;
  // Claves "productId|sizeId" de los productos de ESTE pedido, para vaciar
  // exactamente esos ítems del carrito cuando el financiamiento se apruebe.
  orderedKeys?: string[];
  savedAt: string;
}

export function savePendingSistecredito(
  payment: Omit<PendingSistecreditoPayment, 'savedAt'>,
): void {
  try {
    const data: PendingSistecreditoPayment = {
      ...payment,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(SISTECREDITO_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // almacenamiento no disponible, ignorar
  }
}

export function loadPendingSistecredito(): PendingSistecreditoPayment | null {
  try {
    const raw = localStorage.getItem(SISTECREDITO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingSistecreditoPayment;
    if (!parsed?.order?.orderNumber || !parsed?.contact) return null;
    const savedAt = new Date(parsed.savedAt ?? '').getTime();
    if (!Number.isFinite(savedAt) || Date.now() - savedAt > MAX_AGE_MS) {
      localStorage.removeItem(SISTECREDITO_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingSistecredito(): void {
  try {
    localStorage.removeItem(SISTECREDITO_STORAGE_KEY);
  } catch {
    // ignorar
  }
}